export const getSections = (project, enabledKey = "inbound_enabled") => {
  if (!project) return [];
  const sections = [];

  const addSection = (key, title, rates) => {
    const entries = Object.entries(rates || {}).filter(([, v]) => v?.[enabledKey]);
    if (entries.length) sections.push({ key, title, entries });
  };

  addSection("materialCharges", "Material Charges", project.materialRates);

  addSection("laborCharges", "Labor Charges", project.laborRates);

  addSection("openInspectCharges", "Open & Inspect Charges", project.openInspectRates);

  addSection("equipmentCharges", "Equipment Charges", project.equipmentRates);

  return sections;
};

export const getSectionsWithExistingCharges = (project, enabledKey = "inbound_enabled", shipment = null) => {
  const sections = getSections(project, enabledKey);

  // Add sections for charges that exist in the shipment, even if rates aren't enabled
  const chargeTypes = [
    { key: "materialCharges", title: "Material Charges", rates: project.materialRates },
    { key: "laborCharges", title: "Labor Charges", rates: project.laborRates },
    { key: "openInspectCharges", title: "Open & Inspect Charges", rates: project.openInspectRates },
    { key: "equipmentCharges", title: "Equipment Charges", rates: project.equipmentRates }
  ];

  chargeTypes.forEach(({ key, title, rates }) => {
    if (shipment?.[key] && Object.keys(shipment[key]).length > 0) {
      const shipmentEntries = Object.entries(shipment[key])
        .filter(([rateKey, value]) => {
          // Filter out metadata fields
          const metadataFields = ['calculated_at', 'total'];
          if (metadataFields.includes(rateKey)) return false;
          
          return typeof value === 'object' && value.hasOwnProperty('quantity');
        })
        .map(([rateKey, value]) => [rateKey, value]);

      if (shipmentEntries.length > 0) {
        // Check if section already exists
        const existingSection = sections.find(s => s.key === key);
        
        if (existingSection) {
          const existingKeys = existingSection.entries.map(([rateKey]) => rateKey);
          const newEntries = shipmentEntries.filter(([rateKey]) => !existingKeys.includes(rateKey));
          existingSection.entries.push(...newEntries);
        } else {
          // Add new section
          sections.push({ key, title, entries: shipmentEntries });
        }
      }
    }
  });

  return sections;
};

export const getSectionsWithOneTimeCharges = (project, enabledKey = "inbound_enabled", shipment = null) => {
  const sections = getSectionsWithExistingCharges(project, enabledKey, shipment);
  const metadataFields = ['no_additional_charges', 'total', 'calculated_at'];

  // Add one-time charges section if they exist in the shipment
  if (shipment?.oneTimeCharges) {
    let actualCharges = [];

    if (shipment.oneTimeCharges.list && Array.isArray(shipment.oneTimeCharges.list)) {
      // List format for actual one time charges
      actualCharges = shipment.oneTimeCharges.list.map(item => [item.notes, { quantity: item.amount }]);
    } else {
      // Flat format to denote no additional charges
      actualCharges = Object.entries(shipment.oneTimeCharges)
        .filter(([key]) => !metadataFields.includes(key))
        .map(([key, value]) => [key, { quantity: value }]);
    }

    if (actualCharges.length > 0) {
      sections.push({
        key: "oneTimeCharges",
        title: "One Time Flat Charges",
        entries: actualCharges
      });
    }
  }

  return sections;
};

export const buildInitialValues = (sections, shipment) => {
  return sections.reduce((acc, section) => {
    const sectionValues = section.entries.reduce((obj, [rateKey, entryValue]) => {
      const existingValue = shipment?.[section.key]?.[rateKey];

      // Handle one-time charges differently (they don't have quantity structure)
      if (section.key === "oneTimeCharges") {
        obj[rateKey] = existingValue || entryValue?.quantity || "";
      } else {
        obj[rateKey] = typeof existingValue === "object" ? existingValue.quantity : "";
      }

      return obj;
    }, {});

    acc[section.key] = sectionValues;
    return acc;
  }, {});
};

export const validateCharges = (values, skipValidation = false) => {
  if (skipValidation) return {};

  const errors = {};
  Object.entries(values || {}).forEach(([sectionKey, sectionValues]) => {
    if (!sectionValues || typeof sectionValues !== "object") return;
    const sectionErrors = {};
    Object.entries(sectionValues).forEach(([rateKey, value]) => {
      if (value === "" || value === undefined || value === null) {
        sectionErrors[rateKey] = "Required";
      } else {
        const num = Number(value);
        if (isNaN(num)) {
          sectionErrors[rateKey] = "Must be a number";
        } else if (num < 0) {
          sectionErrors[rateKey] = "Must be 0 or greater";
        }
      }
    });
    if (Object.keys(sectionErrors).length > 0) {
      errors[sectionKey] = sectionErrors;
    }
  });

  return errors;
};
